import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash2, ExternalLink, Copy, LogOut, Users, FileText, Loader2, Check, BarChart3, QrCode, BookOpen, FileBarChart, Activity, UsersRound, Tag, X, Files } from 'lucide-react';
import Logo from '../components/Logo';
import ReportsTab from '../components/ReportsTab';
import InsightsTab from '../components/InsightsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('surveys');
  const [copiedId, setCopiedId] = useState(null);
  const [qrSurvey, setQrSurvey] = useState(null); // {id, name}
  const [duplicateSurvey, setDuplicateSurvey] = useState(null); // {id, name}
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicateError, setDuplicateError] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [surveyScope, setSurveyScope] = useState('all'); // 'all' | 'mine' — admin only
  const [showInactive, setShowInactive] = useState(() => {
    // Persist the preference so users don't have to re-toggle each visit
    try { return localStorage.getItem('tmc.showInactive') === '1'; } catch { return false; }
  });

  const visibleSurveys = showInactive ? surveys : surveys.filter(s => s.active);
  const hiddenInactiveCount = surveys.length - visibleSurveys.length;

  const toggleShowInactive = () => {
    setShowInactive(prev => {
      const next = !prev;
      try { localStorage.setItem('tmc.showInactive', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  useEffect(() => {
    fetchSurveys();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyScope]);

  const fetchSurveys = async () => {
    try {
      const isAdmin = user?.role === 'admin';
      const params = isAdmin && surveyScope === 'mine' ? { scope: 'mine' } : {};
      const { data } = await axios.get(`${API_URL}/api/surveys`, {
        withCredentials: true,
        params,
      });
      setSurveys(data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDelete = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey? All responses will be deleted.')) return;
    
    try {
      await axios.delete(`${API_URL}/api/surveys/${surveyId}`, { withCredentials: true });
      setSurveys(surveys.filter(s => s.id !== surveyId));
    } catch (error) {
      alert('Error deleting survey');
    }
  };

  const copyLink = (surveyId) => {
    const link = `${window.location.origin}/survey/${surveyId}`;
    navigator.clipboard.writeText(link);
    setCopiedId(surveyId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const openDuplicate = (survey) => {
    setDuplicateSurvey({ id: survey.id, name: survey.name });
    // Pre-fill with "{original} (Copy)" so the user just tweaks it.
    setDuplicateName(`${survey.name} (Copy)`);
    setDuplicateError('');
  };

  const closeDuplicate = () => {
    setDuplicateSurvey(null);
    setDuplicateName('');
    setDuplicateError('');
    setDuplicating(false);
  };

  const submitDuplicate = async (e) => {
    e.preventDefault();
    if (!duplicateSurvey) return;
    const trimmed = duplicateName.trim();
    if (!trimmed) {
      setDuplicateError('Please enter a name for the copy.');
      return;
    }
    if (trimmed.toLowerCase() === duplicateSurvey.name.trim().toLowerCase()) {
      setDuplicateError('The new survey must have a different name than the original.');
      return;
    }
    setDuplicating(true);
    setDuplicateError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/surveys/${duplicateSurvey.id}/duplicate`,
        { name: trimmed },
        { withCredentials: true },
      );
      closeDuplicate();
      // Drop the user straight into the editor of the new copy so they can tweak questions.
      navigate(`/admin/surveys/${data.id}/edit`);
    } catch (err) {
      setDuplicateError(err.response?.data?.detail || 'Could not duplicate this survey.');
      setDuplicating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <Logo size="sm" />
              <div className="hidden sm:block">
                <h1 className="text-lg font-medium text-[#2C302E] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  TMC Surveys
                </h1>
                <p className="text-xs text-[#8E958C]">Surveys Made Simple</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#5C635A] hidden md:inline">{user?.email}</span>
              {user?.role === 'admin' ? (
                <button
                  type="button"
                  onClick={() => setActiveTab('users')}
                  className="text-xs px-2 py-1 rounded-full bg-[#7A8B7A]/10 text-[#7A8B7A] uppercase tracking-wide hover:bg-[#7A8B7A]/20 transition-colors"
                  title="Open user management"
                  data-testid="role-badge-admin"
                >
                  Admin
                </button>
              ) : user?.role ? (
                <span className="text-xs px-2 py-1 rounded-full bg-[#7A8B7A]/10 text-[#7A8B7A] uppercase tracking-wide">
                  {user.role}
                </span>
              ) : null}
              <Link
                to="/admin/help"
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#C67D63] hover:bg-[#a96550] text-white text-sm font-medium transition-colors shadow-sm"
                data-testid="open-help-btn"
                title="Open user manual & FAQ"
              >
                <BookOpen size={16} />
                <span className="hidden sm:inline">Help &amp; Manual</span>
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-[#5C635A] hover:text-[#2C302E] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Surveys" value={stats.total_surveys} />
            <StatCard label="Active Surveys" value={stats.active_surveys} />
            <StatCard label="Total Responses" value={stats.total_responses} />
            <StatCard label="Last 7 Days" value={stats.recent_responses} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-[#E5E0D8]">
          <button
            onClick={() => setActiveTab('surveys')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === 'surveys' 
                ? 'border-[#7A8B7A] text-[#2C302E]' 
                : 'border-transparent text-[#5C635A] hover:text-[#2C302E]'
            }`}
            data-testid="tab-surveys"
          >
            <FileText size={18} />
            Surveys
          </button>
          {user?.role === 'admin' && (
            <button
              onClick={() => setActiveTab('users')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-[#7A8B7A] text-[#2C302E]'
                  : 'border-transparent text-[#5C635A] hover:text-[#2C302E]'
              }`}
              data-testid="tab-users"
            >
              <Users size={18} />
              Users
            </button>
          )}
          {user?.role === 'admin' && (
            <button
              onClick={() => setActiveTab('groups')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'groups'
                  ? 'border-[#7A8B7A] text-[#2C302E]'
                  : 'border-transparent text-[#5C635A] hover:text-[#2C302E]'
              }`}
              data-testid="tab-groups"
            >
              <UsersRound size={18} />
              Groups
            </button>
          )}
          <button
            onClick={() => setActiveTab('reports')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
              activeTab === 'reports'
                ? 'border-[#7A8B7A] text-[#2C302E]'
                : 'border-transparent text-[#5C635A] hover:text-[#2C302E]'
            }`}
            data-testid="tab-reports"
          >
            <FileBarChart size={18} />
            Reports
          </button>
          {user?.role === 'admin' && (
            <button
              onClick={() => setActiveTab('insights')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'insights'
                  ? 'border-[#7A8B7A] text-[#2C302E]'
                  : 'border-transparent text-[#5C635A] hover:text-[#2C302E]'
              }`}
              data-testid="tab-insights"
            >
              <Activity size={18} />
              Insights
            </button>
          )}
        </div>

        {/* Content */}
        {activeTab === 'surveys' && (
          <div>
            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {user?.role === 'admin' && surveyScope === 'mine' ? 'My Surveys' : user?.role === 'admin' ? 'All Surveys' : 'Your Surveys'}
                </h2>
                {user?.role === 'admin' && (
                  <div
                    className="inline-flex p-1 bg-[#F4F1EA] rounded-full border border-[#E5E0D8]"
                    role="tablist"
                    aria-label="Survey scope"
                    data-testid="survey-scope-toggle"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={surveyScope === 'all'}
                      onClick={() => setSurveyScope('all')}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        surveyScope === 'all'
                          ? 'bg-white text-[#2C302E] shadow-sm'
                          : 'text-[#5C635A] hover:text-[#2C302E]'
                      }`}
                      data-testid="scope-all"
                    >
                      All surveys
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={surveyScope === 'mine'}
                      onClick={() => setSurveyScope('mine')}
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                        surveyScope === 'mine'
                          ? 'bg-white text-[#2C302E] shadow-sm'
                          : 'text-[#5C635A] hover:text-[#2C302E]'
                      }`}
                      data-testid="scope-mine"
                    >
                      Mine only
                    </button>
                  </div>
                )}
                <label
                  className="inline-flex items-center gap-2 text-xs text-[#5C635A] cursor-pointer select-none"
                  title={showInactive ? 'Hide inactive surveys' : 'Show inactive surveys'}
                  data-testid="show-inactive-toggle-label"
                >
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={toggleShowInactive}
                    className="w-4 h-4 rounded border-[#E5E0D8] text-[#7A8B7A] focus:ring-[#7A8B7A]"
                    data-testid="show-inactive-toggle"
                  />
                  Show inactive
                </label>
              </div>
              <Link
                to="/admin/surveys/new"
                className="flex items-center gap-2 px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full transition-colors"
                data-testid="create-survey-btn"
              >
                <Plus size={18} />
                Create Survey
              </Link>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
              </div>
            ) : visibleSurveys.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-[#E5E0D8]">
                <FileText className="mx-auto text-[#8E958C] mb-4" size={48} />
                {hiddenInactiveCount > 0 ? (
                  <>
                    <p className="text-[#5C635A] mb-2">
                      All {hiddenInactiveCount} survey{hiddenInactiveCount === 1 ? ' is' : 's are'} inactive.
                    </p>
                    <button
                      type="button"
                      onClick={toggleShowInactive}
                      className="text-sm text-[#7A8B7A] hover:text-[#637363] underline"
                      data-testid="empty-show-inactive-cta"
                    >
                      Show inactive surveys
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-[#5C635A] mb-4">No surveys yet</p>
                    <Link
                      to="/admin/surveys/new"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full transition-colors"
                    >
                      <Plus size={18} />
                      Create your first survey
                    </Link>
                  </>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {!showInactive && hiddenInactiveCount > 0 && (
                  <p className="text-xs text-[#8E958C]" data-testid="hidden-inactive-hint">
                    {hiddenInactiveCount} inactive survey{hiddenInactiveCount === 1 ? '' : 's'} hidden.{' '}
                    <button
                      type="button"
                      onClick={toggleShowInactive}
                      className="text-[#7A8B7A] hover:text-[#637363] underline"
                      data-testid="hint-show-inactive"
                    >
                      Show
                    </button>
                  </p>
                )}
                {visibleSurveys.map(survey => (
                  <div
                    key={survey.id}
                    className="bg-white rounded-xl border border-[#E5E0D8] p-6 hover:shadow-sm transition-shadow"
                    data-testid={`survey-card-${survey.id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium text-[#2C302E]">{survey.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            survey.active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {survey.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-[#5C635A] text-sm mb-3">{survey.description}</p>
                        <div className="flex items-center gap-3 text-xs text-[#8E958C]">
                          <span>{survey.questions?.length || 0} questions</span>
                          <span>{survey.response_count || 0} responses</span>
                          {user?.role === 'admin' && (
                            survey.is_mine ? (
                              <span
                                className="px-1.5 py-0.5 rounded-full bg-[#7A8B7A]/15 text-[#637363] uppercase tracking-wide"
                                title="You created this survey"
                                data-testid={`badge-mine-${survey.id}`}
                              >
                                Yours
                              </span>
                            ) : survey.creator_email ? (
                              <span
                                className="text-[#8E958C]"
                                title="Survey owner"
                                data-testid={`owner-${survey.id}`}
                              >
                                · by {survey.creator_email}
                              </span>
                            ) : null
                          )}
                          {!survey.is_mine && user?.role !== 'admin' && (survey.shared_via_groups || []).length > 0 && (
                            <span
                              className="px-1.5 py-0.5 rounded-full bg-[#C67D63]/15 text-[#a96550] uppercase tracking-wide"
                              title={`Shared with you via ${survey.shared_via_groups.map(g => g.name).join(', ')}`}
                              data-testid={`badge-shared-${survey.id}`}
                            >
                              Shared
                            </span>
                          )}
                        </div>
                        {(survey.groups || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`survey-tags-${survey.id}`}>
                            {survey.groups.map(g => (
                              <span
                                key={g.id}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#5C635A] text-xs"
                                data-testid={`survey-tag-${survey.id}-${g.id}`}
                              >
                                <Tag size={10} />
                                {g.name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyLink(survey.id)}
                          className="p-2 text-[#5C635A] hover:text-[#2C302E] hover:bg-[#F4F1EA] rounded-lg transition-colors"
                          title="Copy survey link"
                          data-testid={`copy-link-${survey.id}`}
                        >
                          {copiedId === survey.id ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
                        </button>
                        <button
                          onClick={() => setQrSurvey({ id: survey.id, name: survey.name })}
                          className="p-2 text-[#5C635A] hover:text-[#2C302E] hover:bg-[#F4F1EA] rounded-lg transition-colors"
                          title="Show QR code"
                          data-testid={`qr-${survey.id}`}
                        >
                          <QrCode size={18} />
                        </button>
                        <Link
                          to={`/admin/surveys/${survey.id}/analytics`}
                          className="p-2 text-[#5C635A] hover:text-[#2C302E] hover:bg-[#F4F1EA] rounded-lg transition-colors"
                          title="View analytics"
                          data-testid={`analytics-${survey.id}`}
                        >
                          <BarChart3 size={18} />
                        </Link>
                        <a
                          href={`/survey/${survey.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-[#5C635A] hover:text-[#2C302E] hover:bg-[#F4F1EA] rounded-lg transition-colors"
                          title="Preview survey"
                        >
                          <ExternalLink size={18} />
                        </a>
                        <button
                          type="button"
                          onClick={() => openDuplicate(survey)}
                          className="p-2 text-[#5C635A] hover:text-[#2C302E] hover:bg-[#F4F1EA] rounded-lg transition-colors"
                          title="Duplicate survey"
                          data-testid={`duplicate-survey-${survey.id}`}
                        >
                          <Files size={18} />
                        </button>
                        {(survey.is_mine || user?.role === 'admin') && (
                          <>
                            <Link
                              to={`/admin/surveys/${survey.id}/edit`}
                              className="p-2 text-[#5C635A] hover:text-[#2C302E] hover:bg-[#F4F1EA] rounded-lg transition-colors"
                              title="Edit survey"
                              data-testid={`edit-survey-${survey.id}`}
                            >
                              <Edit size={18} />
                            </Link>
                            <button
                              onClick={() => handleDelete(survey.id)}
                              className="p-2 text-[#5C635A] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete survey"
                              data-testid={`delete-survey-${survey.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'groups' && user?.role === 'admin' && <GroupsTab />}
        {activeTab === 'reports' && <ReportsTab />}
        {activeTab === 'insights' && user?.role === 'admin' && <InsightsTab />}
      </div>

      {qrSurvey && (
        <QrModal survey={qrSurvey} onClose={() => setQrSurvey(null)} />
      )}

      {duplicateSurvey && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closeDuplicate}
          data-testid="duplicate-modal-backdrop"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitDuplicate}
            className="bg-white rounded-2xl border border-[#E5E0D8] shadow-xl max-w-md w-full p-6"
            data-testid="duplicate-modal"
          >
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <h3 className="text-lg font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Duplicate survey
                </h3>
                <p className="text-sm text-[#5C635A] mt-1">
                  Copies questions, description, and theme from{' '}
                  <strong>{duplicateSurvey.name}</strong>. Responses, group tags, and the
                  email schedule do <em>not</em> carry over — you start with a clean slate
                  you own.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDuplicate}
                className="p-1 text-[#8E958C] hover:text-[#2C302E] rounded-full hover:bg-[#F4F1EA]"
                aria-label="Close"
                data-testid="duplicate-close"
              >
                <X size={18} />
              </button>
            </div>

            <label className="block text-xs uppercase tracking-wide text-[#8E958C] mb-1.5" htmlFor="duplicate-name-input">
              New survey name
            </label>
            <input
              id="duplicate-name-input"
              type="text"
              autoFocus
              value={duplicateName}
              onChange={(e) => setDuplicateName(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 text-[#2C302E]"
              placeholder="e.g., Q1 Patient Pulse (Copy)"
              data-testid="duplicate-name-input"
            />
            {duplicateError && (
              <p className="text-sm text-red-600 mt-2" data-testid="duplicate-error">
                {duplicateError}
              </p>
            )}

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={closeDuplicate}
                className="px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm transition-colors"
                data-testid="duplicate-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={duplicating}
                className="px-4 py-2 rounded-full bg-[#7A8B7A] hover:bg-[#637363] text-white text-sm transition-colors disabled:opacity-60 flex items-center gap-2"
                data-testid="duplicate-submit"
              >
                {duplicating && <Loader2 size={14} className="animate-spin" />}
                {duplicating ? 'Duplicating…' : 'Create copy'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const QrModal = ({ survey, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState('');
  const publicUrl = `${window.location.origin}/survey/${survey.id}`;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let revoked = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/surveys/${survey.id}/qr`, {
          responseType: 'blob',
          withCredentials: true,
        });
        if (!revoked) {
          createdUrl = URL.createObjectURL(res.data);
          setBlobUrl(createdUrl);
        }
      } catch (e) {
        setError('Failed to generate QR code');
      }
    })();
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [survey.id]);

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center px-4 z-50"
      onClick={onClose}
      data-testid="qr-modal"
    >
      <div
        className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Share Survey
            </h3>
            <p className="text-sm text-[#5C635A] mt-1">{survey.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#8E958C] hover:text-[#2C302E] text-2xl leading-none"
            data-testid="qr-modal-close"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-xl p-4 flex items-center justify-center mb-4">
          {error ? (
            <p className="text-red-600 text-sm py-12">{error}</p>
          ) : blobUrl ? (
            <img src={blobUrl} alt="Survey QR code" className="w-64 h-64" data-testid="qr-image" />
          ) : (
            <div className="py-12">
              <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className="block text-xs uppercase tracking-wide text-[#8E958C] mb-1">Public URL</label>
          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={publicUrl}
              className="flex-1 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-[#FDFBF7] text-sm text-[#2C302E]"
              data-testid="qr-public-url"
              onFocus={(e) => e.target.select()}
            />
            <button
              onClick={copyUrl}
              className="px-3 py-2 rounded-lg border border-[#E5E0D8] hover:bg-[#F4F1EA] text-[#2C302E] text-sm"
              data-testid="qr-copy-url"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {blobUrl && (
          <a
            href={blobUrl}
            download={`${survey.name.replace(/\s+/g, '_')}_qr.png`}
            className="block w-full text-center py-2.5 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full font-medium transition-colors"
            data-testid="qr-download"
          >
            Download PNG
          </a>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white rounded-xl border border-[#E5E0D8] p-4">
    <p className="text-sm text-[#5C635A] mb-1">{label}</p>
    <p className="text-2xl font-bold text-[#2C302E]">{value}</p>
  </div>
);

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'creator' });
  const [savingRoleId, setSavingRoleId] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/users`,
        // password intentionally omitted — SSO users authenticate via Microsoft
        { email: newUser.email, name: newUser.name, role: newUser.role },
        { withCredentials: true }
      );
      setUsers([...users, data]);
      setShowAddForm(false);
      setNewUser({ email: '', name: '', role: 'creator' });
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating user');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setSavingRoleId(userId);
    try {
      await axios.patch(
        `${API_URL}/api/admin/users/${userId}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      setUsers(users.map(u => (u.id === userId ? { ...u, role: newRole } : u)));
    } catch (error) {
      alert(error.response?.data?.detail || 'Error updating role');
    } finally {
      setSavingRoleId(null);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`, { withCredentials: true });
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting user');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Users
        </h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full transition-colors"
          data-testid="add-user-btn"
        >
          <Plus size={18} />
          Add User
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-xl border border-[#E5E0D8] p-6 mb-6">
          <h3 className="text-lg font-medium text-[#2C302E] mb-1">Invite a user</h3>
          <p className="text-sm text-[#5C635A] mb-4">
            They sign in with their Microsoft account — no password needed here.
            Their <strong>email</strong> must match their Entra ID sign-in.
          </p>
          <form onSubmit={handleAddUser} className="grid gap-4 sm:grid-cols-3">
            <input
              type="text"
              placeholder="Full name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
              required
              data-testid="new-user-name"
            />
            <input
              type="email"
              placeholder="user@therapymgmt.com"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
              required
              data-testid="new-user-email"
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="px-4 py-2 rounded-lg border border-[#E5E0D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
              data-testid="new-user-role"
            >
              <option value="creator">Creator — manages their own surveys</option>
              <option value="admin">Admin — full access</option>
            </select>
            <div className="sm:col-span-3 flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full transition-colors"
                data-testid="submit-new-user"
              >
                Add User
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewUser({ email: '', name: '', role: 'creator' });
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E5E0D8] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F4F1EA]">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-[#5C635A]">Name</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-[#5C635A]">Email</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-[#5C635A]">Role</th>
              <th className="px-6 py-3 text-right text-sm font-medium text-[#5C635A]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E0D8]">
            {users.map(u => {
              const isSelf = u.id === currentUser?.id;
              return (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="px-6 py-4 text-[#2C302E]">{u.name}</td>
                  <td className="px-6 py-4 text-[#5C635A]">{u.email}</td>
                  <td className="px-6 py-4">
                    <select
                      value={u.role || 'creator'}
                      disabled={isSelf || savingRoleId === u.id}
                      onChange={(e) => handleRoleChange(u.id, e.target.value)}
                      className="px-3 py-1.5 rounded-full border border-[#E5E0D8] bg-white text-xs uppercase tracking-wide text-[#2C302E] disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                      data-testid={`role-select-${u.id}`}
                      title={isSelf ? 'You cannot change your own role' : 'Change role'}
                    >
                      <option value="creator">Creator</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {!isSelf && (
                      <button
                        onClick={() => handleDelete(u.id)}
                        className="text-[#5C635A] hover:text-red-600 transition-colors"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const GroupsTab = () => {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '' });
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({ name: '', description: '' });
  const [addMemberSelect, setAddMemberSelect] = useState({}); // {groupId: userId}
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    Promise.all([fetchGroups(), fetchUsers()]).finally(() => setLoading(false));
  }, []);

  const fetchGroups = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/groups`, { withCredentials: true });
      setGroups(data);
    } catch (e) {
      console.error('Error fetching groups:', e);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/groups`,
        { name: newGroup.name, description: newGroup.description },
        { withCredentials: true }
      );
      setGroups([...groups, data]);
      setShowAddForm(false);
      setNewGroup({ name: '', description: '' });
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Could not create group');
    }
  };

  const handleDelete = async (groupId, name) => {
    if (!window.confirm(`Delete the "${name}" group? Members will lose access to any surveys tagged with it. Surveys themselves are not deleted.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/groups/${groupId}`, { withCredentials: true });
      setGroups(groups.filter(g => g.id !== groupId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete group');
    }
  };

  const startEdit = (group) => {
    setEditingId(group.id);
    setEditValues({ name: group.name, description: group.description || '' });
  };

  const saveEdit = async (groupId) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/groups/${groupId}`,
        { name: editValues.name, description: editValues.description },
        { withCredentials: true }
      );
      setGroups(groups.map(g => g.id === groupId ? { ...g, name: editValues.name, description: editValues.description } : g));
      setEditingId(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not update group');
    }
  };

  const addMember = async (groupId) => {
    const userId = addMemberSelect[groupId];
    if (!userId) return;
    try {
      const { data: member } = await axios.post(
        `${API_URL}/api/admin/groups/${groupId}/members`,
        { user_id: userId },
        { withCredentials: true }
      );
      setGroups(groups.map(g => g.id === groupId
        ? { ...g, members: [...(g.members || []), member], member_count: (g.member_count || 0) + 1 }
        : g
      ));
      setAddMemberSelect({ ...addMemberSelect, [groupId]: '' });
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not add member');
    }
  };

  const removeMember = async (groupId, userId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/groups/${groupId}/members/${userId}`, { withCredentials: true });
      setGroups(groups.map(g => g.id === groupId
        ? { ...g, members: (g.members || []).filter(m => m.id !== userId), member_count: Math.max(0, (g.member_count || 1) - 1) }
        : g
      ));
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not remove member');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-[#7A8B7A]" size={32} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-start mb-2 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-medium text-[#2C302E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Groups
          </h2>
          <p className="text-sm text-[#5C635A] mt-1 max-w-2xl">
            Create groups (e.g., <em>Branding</em>, <em>Operations</em>) and add creators to them.
            Anyone in a group sees every survey tagged with that group on their dashboard and can run reports from it.
          </p>
        </div>
        <button
          onClick={() => { setShowAddForm(true); setErrorMsg(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full transition-colors"
          data-testid="add-group-btn"
        >
          <Plus size={18} />
          New Group
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-xl border border-[#E5E0D8] p-6 my-4">
          <h3 className="text-lg font-medium text-[#2C302E] mb-4">Create a new group</h3>
          {errorMsg && <p className="text-sm text-red-600 mb-3" data-testid="group-form-error">{errorMsg}</p>}
          <form onSubmit={handleCreate} className="grid gap-4">
            <input
              type="text"
              placeholder="Group name (e.g., Branding)"
              value={newGroup.name}
              onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
              className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
              required
              data-testid="new-group-name"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={newGroup.description}
              onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
              className="px-4 py-2 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
              data-testid="new-group-description"
            />
            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full transition-colors"
                data-testid="submit-new-group"
              >
                Create Group
              </button>
              <button
                type="button"
                onClick={() => { setShowAddForm(false); setNewGroup({ name: '', description: '' }); setErrorMsg(''); }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {groups.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E5E0D8]" data-testid="no-groups-empty">
          <UsersRound className="mx-auto text-[#8E958C] mb-4" size={48} />
          <p className="text-[#5C635A]">No groups yet. Create your first group to start sharing surveys.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {groups.map(group => {
            const memberIds = new Set((group.members || []).map(m => m.id));
            const candidates = users.filter(u => !memberIds.has(u.id));
            return (
              <div
                key={group.id}
                className="bg-white rounded-xl border border-[#E5E0D8] p-6"
                data-testid={`group-card-${group.id}`}
              >
                <div className="flex justify-between items-start gap-4 mb-3">
                  <div className="flex-1">
                    {editingId === group.id ? (
                      <div className="grid gap-2">
                        <input
                          type="text"
                          value={editValues.name}
                          onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                          className="px-3 py-1.5 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 font-medium"
                          data-testid={`edit-group-name-${group.id}`}
                        />
                        <input
                          type="text"
                          value={editValues.description}
                          onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                          placeholder="Description"
                          className="px-3 py-1.5 rounded-lg border border-[#E5E0D8] focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 text-sm"
                          data-testid={`edit-group-description-${group.id}`}
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => saveEdit(group.id)}
                            className="px-3 py-1 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full text-sm"
                            data-testid={`save-group-${group.id}`}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingId(null)}
                            className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <h3 className="text-lg font-medium text-[#2C302E] flex items-center gap-2">
                          <Tag size={16} className="text-[#7A8B7A]" />
                          {group.name}
                        </h3>
                        {group.description && (
                          <p className="text-sm text-[#5C635A] mt-1">{group.description}</p>
                        )}
                        <p className="text-xs text-[#8E958C] mt-1">
                          {group.member_count || 0} member{(group.member_count || 0) === 1 ? '' : 's'}
                        </p>
                      </>
                    )}
                  </div>
                  {editingId !== group.id && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(group)}
                        className="p-2 text-[#5C635A] hover:text-[#2C302E] hover:bg-[#F4F1EA] rounded-lg transition-colors"
                        title="Rename group"
                        data-testid={`edit-group-${group.id}`}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(group.id, group.name)}
                        className="p-2 text-[#5C635A] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete group"
                        data-testid={`delete-group-${group.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Member chips */}
                <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`group-members-${group.id}`}>
                  {(group.members || []).map(m => (
                    <span
                      key={m.id}
                      className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-[#F4F1EA] text-[#2C302E] text-sm"
                      data-testid={`member-chip-${group.id}-${m.id}`}
                    >
                      {m.email}
                      <button
                        type="button"
                        onClick={() => removeMember(group.id, m.id)}
                        className="p-0.5 rounded-full hover:bg-[#E5E0D8]"
                        title="Remove member"
                        data-testid={`remove-member-${group.id}-${m.id}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                  {(group.members || []).length === 0 && (
                    <span className="text-xs text-[#8E958C]">No members yet.</span>
                  )}
                </div>

                {/* Add member */}
                {candidates.length > 0 && (
                  <div className="flex gap-2">
                    <select
                      value={addMemberSelect[group.id] || ''}
                      onChange={(e) => setAddMemberSelect({ ...addMemberSelect, [group.id]: e.target.value })}
                      className="flex-1 sm:flex-none sm:w-72 px-3 py-2 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20"
                      data-testid={`add-member-select-${group.id}`}
                    >
                      <option value="">Add a member…</option>
                      {candidates.map(u => (
                        <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => addMember(group.id)}
                      disabled={!addMemberSelect[group.id]}
                      className="px-3 py-2 rounded-lg bg-[#7A8B7A] hover:bg-[#637363] text-white text-sm disabled:opacity-50"
                      data-testid={`add-member-btn-${group.id}`}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
